/**
 * src/components/teacher-dashboard-components/theme/components/Typography.js
 *
 * React Component Module.
 *
 * Purpose: React UI module containing one or more components and local UI helpers.
 *
 * Data flow (high level):
 * - UI/Caller -> this module -> downstream helpers/services -> state/response.
 */
import React from 'react';
import styled from 'styled-components';
import { useTheme } from '../ThemeContext';
import { getColor, getTypography } from '../themeUtils';

/**
 * Map Transient Typography Props.
 *
 * React component or UI helper used to render and manage local UI interactions.
 */
const mapTransientTypographyProps = ({
  color,
  align,
  weight,
  transform,
  decoration,
  margin,
  marginTop,
  marginBottom,
  marginLeft,
  marginRight,
  gradient,
  truncate,
  lines,
  ...rest
}) => ({
  ...rest,
  $color: color,
  $align: align,
  $weight: weight,
  $transform: transform,
  $decoration: decoration,
  $margin: margin,
  $marginTop: marginTop,
  $marginBottom: marginBottom,
  $marginLeft: marginLeft,
  $marginRight: marginRight,
  $gradient: gradient,
  $truncate: truncate,
  $lines: lines
});

/**
 * Create Typography Component.
 *
 * React component or UI helper used to render and manage local UI interactions.
 */
const createTypographyComponent = (variant, as = 'p') => {
  return styled(as)`
    margin: 0;
    font-family: ${props => props.theme.typography.fontFamily.primary};
    color: ${props => getColor(props.$color || 'neutral.black')};
    
    ${props => {
      const typography = getTypography(variant, props.theme.isMobile);
      return `
        font-size: ${typography.fontSize};
        font-weight: ${typography.fontWeight};
        line-height: ${typography.lineHeight};
        letter-spacing: ${typography.letterSpacing || 'normal'};
      `;
    }}
    
    ${props => props.$align && `text-align: ${props.$align};`}
    ${props => props.$weight && `font-weight: ${props.$weight};`}
    ${props => props.$transform && `text-transform: ${props.$transform};`}
    ${props => props.$decoration && `text-decoration: ${props.$decoration};`}
    ${props => props.$margin && `margin: ${props.$margin};`}
    ${props => props.$marginTop && `margin-top: ${props.$marginTop};`}
    ${props => props.$marginBottom && `margin-bottom: ${props.$marginBottom};`}
    ${props => props.$marginLeft && `margin-left: ${props.$marginLeft};`}
    ${props => props.$marginRight && `margin-right: ${props.$marginRight};`}
    
    ${props => props.$gradient && `
      background: ${props.theme.colors.gradients.primary};
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    `}
    
    ${props => props.$truncate && `
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    `}
    
    ${props => props.$lines && `
      display: -webkit-box;
      -webkit-line-clamp: ${props.$lines};
      -webkit-box-orient: vertical;
      overflow: hidden;
    `}
  `;
};

const StyledH1 = createTypographyComponent('h1', 'h1');
const StyledH2 = createTypographyComponent('h2', 'h2');
const StyledH3 = createTypographyComponent('h3', 'h3');
const StyledH4 = createTypographyComponent('h4', 'h4');
const StyledBody = createTypographyComponent('body', 'p');
const StyledSmall = createTypographyComponent('small', 'small');
const StyledCaption = createTypographyComponent('caption', 'span');

/**
 * Typography.
 *
 * React component or UI helper used to render and manage local UI interactions.
 */
const Typography = ({
  variant = 'body',
  as,
  children,
  className,
  ...props
}) => {
  const theme = useTheme();
  
    /**
   * Get Component.
   *
   * React component or UI helper used to render and manage local UI interactions.
   */
   const getComponent = () => {
    switch (variant) {
      case 'h1':
        return StyledH1;
      case 'h2':
        return StyledH2;
      case 'h3':
        return StyledH3;
      case 'h4':
        return StyledH4;
      case 'body':
        return StyledBody;
      case 'small':
        return StyledSmall;
      case 'caption':
        return StyledCaption;
      default:
        return StyledBody;
    }
  };
  
  const Component = getComponent();
  const elementType = as || (variant.startsWith('h') ? variant : 'p');
  
  return (
    <Component
      as={elementType}
      theme={theme}
      className={className}
      {...mapTransientTypographyProps(props)}
    >
      {children}
    </Component>
  );
};

/**
 * H1 Component.
 *
 * React component or UI helper used to render and manage local UI interactions.
 */
    const H1Component = ({ children, className, ...props }) => {
  const theme = useTheme();
  return (
    <StyledH1 theme={theme} className={className} {...mapTransientTypographyProps(props)}>
      {children}
    </StyledH1>
  );
};

/**
 * H2 Component.
 *
 * React component or UI helper used to render and manage local UI interactions.
 */
    const H2Component = ({ children, className, ...props }) => {
  const theme = useTheme();
  return (
    <StyledH2 theme={theme} className={className} {...mapTransientTypographyProps(props)}>
      {children}
    </StyledH2>
  );
};

/**
 * H3 Component.
 *
 * React component or UI helper used to render and manage local UI interactions.
 */
    const H3Component = ({ children, className, ...props }) => {
  const theme = useTheme();
  return (
    <StyledH3 theme={theme} className={className} {...mapTransientTypographyProps(props)}>
      {children}
    </StyledH3>
  );
};

/**
 * H4 Component.
 *
 * React component or UI helper used to render and manage local UI interactions.
 */
const H4Component = ({ children, className, ...props }) => {
  const theme = useTheme();
  return (
    <StyledH4 theme={theme} className={className} {...mapTransientTypographyProps(props)}>
      {children}
    </StyledH4>
  );
};

/**
 * Body Component.
 *
 * React component or UI helper used to render and manage local UI interactions.
 */
const BodyComponent = ({ children, className, ...props }) => {
  const theme = useTheme();
  return (
    <StyledBody theme={theme} className={className} {...mapTransientTypographyProps(props)}>
      {children}
    </StyledBody>
  );
};

/**
 * Small Component.
 *
 * React component or UI helper used to render and manage local UI interactions.
 */
const SmallComponent = ({ children, className, ...props }) => {
  const theme = useTheme();
  return (
    <StyledSmall theme={theme} className={className} {...mapTransientTypographyProps(props)}>
      {children}
    </StyledSmall>
  );
};

/**
 * Caption Component.
 *
 * React component or UI helper used to render and manage local UI interactions.
 */
const CaptionComponent = ({ children, className, ...props }) => {
  const theme = useTheme();
  return (
    <StyledCaption theme={theme} className={className} {...mapTransientTypographyProps(props)}>
      {children}
    </StyledCaption>
  );
};

Typography.H1 = H1Component;
Typography.H2 = H2Component;
Typography.H3 = H3Component;
Typography.H4 = H4Component;
Typography.Body = BodyComponent;
Typography.Small = SmallComponent;
Typography.Caption = CaptionComponent;

export default Typography;
